import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import Fuse from 'fuse.js';
import Iconify from '../components/iconify';
import * as docsService from '../services/docsService';
import { errorNotification } from '../services/notificationService';
import { buildMarkdownComponents } from '../components/help/calloutMarkdownComponents';

export default function HelpPage() {
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await docsService.getSearchIndex();
        setArticles(Array.isArray(data?.articles) ? data.articles : []);
      } catch (error) {
        errorNotification('Failed to load help articles');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openArticleBySlug = useCallback(
    (slug) => {
      const article = articles.find((a) => a.id === slug);
      if (article) setSelectedArticle(article);
    },
    [articles]
  );

  const openArticle = useCallback(
    (article) => {
      openArticleBySlug(article.id);
    },
    [openArticleBySlug]
  );

  const handleDownloadWorkbook = async () => {
    try {
      setDownloading(true);
      await docsService.downloadWorkbook();
    } catch (error) {
      errorNotification('Failed to download workbook');
    } finally {
      setDownloading(false);
    }
  };

  const goBack = () => {
    setSelectedArticle(null);
  };

  const fuse = useMemo(
    () =>
      new Fuse(articles, {
        keys: [
          { name: 'title', weight: 0.4 },
          { name: 'summary', weight: 0.25 },
          { name: 'tags', weight: 0.2 },
          { name: 'body', weight: 0.15 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [articles]
  );

  const results = query.trim() ? fuse.search(query.trim()).map((r) => r.item) : null;

  const markdownComponents = useMemo(
    () => buildMarkdownComponents({ onOpenSlug: openArticleBySlug }),
    [openArticleBySlug]
  );

  // Group articles by category (empty-query state)
  const categories = {};
  articles.forEach((article) => {
    const cat = article.category || 'General';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(article);
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Article detail view
  if (selectedArticle) {
    return (
      <>
        <Helmet>
          <title>{selectedArticle.title} | Help</title>
        </Helmet>
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <IconButton onClick={goBack} sx={{ mr: 1 }}>
              <Iconify icon="eva:arrow-back-fill" />
            </IconButton>
            <Typography variant="h4">{selectedArticle.title}</Typography>
          </Stack>
          <Card>
            <CardContent sx={{ '& img': { maxWidth: '100%' }, '& h2': { mt: 3 }, '& p': { lineHeight: 1.8 } }}>
              <ReactMarkdown components={markdownComponents}>{selectedArticle.body}</ReactMarkdown>
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  // Article list view
  return (
    <>
      <Helmet>
        <title>Help & Guides</title>
      </Helmet>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 4 }}>
          Help & Guides
        </Typography>

        <Card sx={{ mb: 4 }}>
          <CardActionArea onClick={handleDownloadWorkbook} disabled={downloading}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify icon="eva:file-text-outline" width={36} sx={{ color: 'primary.main' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">Download Analysis Workbook</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Excel workbook for analyzing exported sensor CSV data
                  </Typography>
                </Box>
                {downloading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Iconify icon="eva:download-outline" width={24} />
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>

        <TextField
          fullWidth
          placeholder="Search guides…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 4 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" />
              </InputAdornment>
            ),
          }}
        />

        {results !== null ? (
          results.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No guides match &ldquo;{query.trim()}&rdquo;.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {results.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card>
                    <CardActionArea onClick={() => openArticle(article)}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {article.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {article.summary}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {article.tags?.slice(0, 3).map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )
        ) : (
          Object.entries(categories).map(([category, categoryArticles]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                {category}
              </Typography>
              <Grid container spacing={2}>
                {categoryArticles.map((article) => (
                  <Grid item xs={12} sm={6} md={4} key={article.id}>
                    <Card>
                      <CardActionArea onClick={() => openArticle(article)}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {article.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {article.summary}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {article.tags?.slice(0, 3).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))
        )}
      </Container>
    </>
  );
}
